// client/src/pages/HomePage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Typography, Grid, Button, Box, Alert, Paper, Stack, TextField,
  FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel, Chip,
} from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import Banner from '../components/Banner';
import ProductSkeleton from '../components/ProductSkeleton';
import ProductCarousel from '../components/ProductCarousel';
import ProductCard from '../components/ProductCard';
import EmptyState from '../components/EmptyState';
import { useCategories } from '../context/CategoryContext';
import { getRecentlyViewed } from '../utils/recentlyViewed';
import { API_BASE_URL } from '../utils/media';

const SORT_OPTIONS = [
  { value: '', label: 'Most Relevant' },
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'name_asc', label: 'Name: A to Z' },
];

const HomePage = () => {
  const { subcategoriesFor } = useCategories();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const subcategory = searchParams.get('subcategory') || '';
  const priceMin = searchParams.get('priceMin') || '';
  const priceMax = searchParams.get('priceMax') || '';
  const inStock = searchParams.get('inStock') === 'true';
  const sort = searchParams.get('sort') || '';
  const sale = searchParams.get('sale') === 'true';
  const hasFilters = Boolean(search || category || subcategory || priceMin || priceMax || inStock || sort || sale);

  const [priceMinInput, setPriceMinInput] = useState(priceMin);
  const [priceMaxInput, setPriceMaxInput] = useState(priceMax);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentlyViewed] = useState(() => getRecentlyViewed());

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category) params.set('category', category);
        if (subcategory) params.set('subcategory', subcategory);
        if (priceMin) params.set('priceMin', priceMin);
        if (priceMax) params.set('priceMax', priceMax);
        if (inStock) params.set('inStock', 'true');
        if (sort) params.set('sort', sort);
        if (sale) params.set('sale', 'true');
        const response = await axios.get(`${API_BASE_URL}/api/products?${params.toString()}`);
        // A non-array response (e.g. the SPA's own index.html coming back
        // instead of JSON, if a reverse-proxy rule isn't matching /api/...)
        // means something upstream is misconfigured — treat it as a failure
        // instead of silently feeding garbage into the product carousels.
        if (!Array.isArray(response.data)) {
          throw new Error('Unexpected non-array response from /api/products');
        }
        setProducts(response.data);
      } catch (err) {
        setError('Failed to fetch products. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [search, category, subcategory, priceMin, priceMax, inStock, sort, sale]);

  // Debounce the price inputs before pushing them into the URL / triggering a fetch.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (priceMinInput === priceMin && priceMaxInput === priceMax) return;
      const next = new URLSearchParams(searchParams);
      if (priceMinInput) next.set('priceMin', priceMinInput); else next.delete('priceMin');
      if (priceMaxInput) next.set('priceMax', priceMaxInput); else next.delete('priceMax');
      setSearchParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceMinInput, priceMaxInput]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  // Shuffled once per product list, not on every render - otherwise typing
  // in the price filter reshuffles the whole "Popular" carousel.
  const popularProducts = useMemo(
    () => [...products].sort(() => 0.5 - Math.random()).slice(0, 12),
    [products]
  );

  if (loading) return <ProductSkeleton />;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;

  if (hasFilters) {
    return (
      <Container maxWidth={false} sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {search ? `Results for "${search}"` : category ? `Category: ${category}${subcategory ? ` / ${subcategory}` : ''}` : sale ? 'Specials' : sort === 'newest' ? 'New Arrivals' : 'All Products'}
            {search && category ? ` in ${category}${subcategory ? ` / ${subcategory}` : ''}` : ''}
            {' '}({products.length})
          </Typography>
          <Button component={Link} to="/">Clear all filters</Button>
        </Box>

        {category && subcategoriesFor(category).length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip
              label="All"
              size="small"
              color={subcategory ? 'default' : 'primary'}
              variant={subcategory ? 'outlined' : 'filled'}
              onClick={() => updateParam('subcategory', '')}
            />
            {subcategoriesFor(category).map((sub) => (
              <Chip
                key={sub}
                label={sub}
                size="small"
                color={subcategory === sub ? 'primary' : 'default'}
                variant={subcategory === sub ? 'filled' : 'outlined'}
                onClick={() => updateParam('subcategory', sub)}
              />
            ))}
          </Stack>
        )}

        <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={3} xl={2} sx={{ position: { md: 'sticky' }, top: { md: 96 } }}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>Filters</Typography>
          <Stack spacing={2.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Sort by</InputLabel>
              <Select label="Sort by" value={sort} onChange={(e) => updateParam('sort', e.target.value)}>
                {SORT_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Price</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" type="number" placeholder="Min" value={priceMinInput} onChange={(e) => setPriceMinInput(e.target.value)} fullWidth />
                <Typography color="text.secondary">–</Typography>
                <TextField size="small" type="number" placeholder="Max" value={priceMaxInput} onChange={(e) => setPriceMaxInput(e.target.value)} fullWidth />
              </Stack>
            </Box>

            <FormControlLabel
              control={<Checkbox checked={inStock} onChange={(e) => updateParam('inStock', e.target.checked ? 'true' : '')} />}
              label="In stock only"
            />

            <Button component={Link} to="/" size="small" variant="outlined">Clear all filters</Button>
          </Stack>
        </Paper>
        </Grid>

        <Grid item xs={12} md={9} xl={10}>
        {products.length === 0 ? (
          <EmptyState
            icon={<SearchOffIcon />}
            title="No products matched your filters"
            description="Try widening your price range or clearing a filter."
            actionLabel="Clear all filters"
            actionTo="/"
          />
        ) : (
          <Grid container spacing={2.5}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={product.id}>
                <ProductCard product={product} />
              </Grid>
            ))}
          </Grid>
        )}
        </Grid>
        </Grid>
      </Container>
    );
  }

  const trendingProducts = products.slice(0, 12);
  const exploreNextProducts = [...products].reverse().slice(0, 12);
  const inspiredProducts = products.slice(1, 13);

  return (
    <div>
      <Banner />
      {recentlyViewed.length > 0 && <ProductCarousel title="Recently Viewed" products={recentlyViewed} />}
      <ProductCarousel title="Pick Up Where You Left Off" products={trendingProducts} />
      <ProductCarousel title="Trending Now" products={exploreNextProducts} />
      <ProductCarousel title="What to Explore Next" products={inspiredProducts} />
      <ProductCarousel title="Inspired By Your Browsing" products={popularProducts} />
    </div>
  );
};

export default HomePage;
